import styled from "styled-components";

export const INITIAL_POSITION = {
    x: 24,
    y: 88
};

export const WINDOW_WIDTH = 980;
export const WINDOW_HEIGHT = 720;
export const MINIMIZED_WIDTH = 360;

export const FloatingWindow = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1200;
    width: ${p => p.$isMinimized ? `${MINIMIZED_WIDTH}px` : `min(${WINDOW_WIDTH}px, calc(100vw - 2rem))`};
    max-height: calc(100vh - 2rem);
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 0.75rem;
    background: #16181d;
    box-shadow: 0 1.25rem 3rem rgba(15, 23, 42, 0.32);
    color: #fff;
    overflow: hidden;
    transform: translate(${p => p.$x}px, ${p => p.$y}px);

    ${p => p.$centered && `
        top: 50%;
        left: 50%;
        width: min(26rem, calc(100vw - 2rem));
        max-height: calc(100vh - 2rem);
        transform: translate(-50%, -50%);
    `}

    ${p => p.$dedicated && `
        position: static;
        width: 100%;
        height: 100vh;
        min-height: 100vh;
        max-height: none;
        border: 0;
        border-radius: 0;
        box-shadow: none;
        display: grid;
        grid-template-rows: auto minmax(0, 1fr);
        transform: none;
    `}

    @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            scroll-behavior: auto !important;
            transition-duration: 0.01ms !important;
        }
    }
`;

export const WindowHeader = styled.div`
    box-sizing: border-box;
    min-height: 3.25rem;
    padding: 0.75rem 0.875rem;
    background: #0b0c10;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    cursor: grab;
    user-select: none;

    &:active {
        cursor: grabbing;
    }
`;

export const HeaderInfo = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.125rem;
`;

export const Eyebrow = styled.div`
    color: rgba(255, 255, 255, 0.64);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.6875rem;
    font-weight: 800;
    text-transform: uppercase;
`;

export const Title = styled.div`
    color: #fff;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.9375rem;
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
`;

export const HeaderButton = styled.button`
    width: 2rem;
    height: 2rem;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    opacity: ${p => p.disabled ? 0.55 : 1};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 1.125rem;
    font-weight: 900;
    line-height: 1;

    &:hover {
        background: rgba(255, 255, 255, 0.18);
    }
`;

export const CloseButtonIcon = styled.img`
    width: 0.875rem;
    height: 0.875rem;
    display: block;
    filter: invert(1);
`;

export const WindowBody = styled.div`
    max-height: ${p => p.$dedicated ? "calc(100vh - 3.25rem)" : "calc(100vh - 5.25rem)"};
    min-height: ${p => p.$dedicated ? "calc(100vh - 3.25rem)" : "auto"};
    overflow: auto;
    background: linear-gradient(180deg, #22252b 0%, #15171c 100%);

    ${p => p.$dedicated && `
        width: 100%;
        height: 100%;
        max-height: none;
        min-height: 0;
        overflow: hidden;
    `}
`;

export const LiveCallShell = styled.div`
    position: relative;
    min-height: ${p => p.$dedicated ? "calc(100vh - 3.25rem)" : "auto"};
    height: ${p => p.$dedicated ? "100%" : "auto"};
    overflow: hidden;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr) auto;
    background: #15171c;
`;

export const LiveCallHeaderBar = styled.div`
    min-width: 0;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(11, 12, 16, 0.82);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
`;

export const LiveCallIdentity = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
`;

export const LiveCallLogo = styled.div`
    width: 2rem;
    height: 2rem;
    flex: 0 0 2rem;
    border-radius: 0.5rem;
    overflow: hidden;
    background: ${p => p.$hasImage || p.$brand
        ? "#fff"
        : "linear-gradient(135deg, #0B66E4 0%, #7C3AED 100%)"};
    color: #fff;
    display: grid;
    place-items: center;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.75rem;
    font-weight: 900;

    img {
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        object-fit: ${p => p.$hasImage ? "cover" : "contain"};
        padding: ${p => p.$hasImage ? "0" : "0.25rem"};
    }
`;

export const LiveCallTitleGroup = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.125rem;
`;

export const LiveCallTitle = styled.div`
    color: #fff;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.9375rem;
    font-weight: 900;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const LiveCallSubtitle = styled.div`
    color: rgba(255, 255, 255, 0.58);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.75rem;
    font-weight: 800;
`;

export const LiveCallMeta = styled.div`
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: rgba(255, 255, 255, 0.74);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.8125rem;
    font-weight: 900;
`;

export const ConnectionDot = styled.span`
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 999px;
    background: ${p => p.$connected ? "#22C55E" : "#F59E0B"};
    box-shadow: 0 0 0 0.25rem ${p => p.$connected ? "rgba(34, 197, 94, 0.14)" : "rgba(245, 158, 11, 0.14)"};
`;

export const LiveCallContent = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) ${p => p.$notesCollapsed ? "2.75rem" : "360px"};
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

export const LiveCallMain = styled.div`
    min-width: 0;
    min-height: 0;
    overflow: hidden;
`;

export const LiveCallOverlayPanel = styled.div`
    position: absolute;
    left: 50%;
    bottom: calc(100% + 0.75rem);
    z-index: 12;
    width: min(28rem, calc(100% - 2rem));
    max-height: min(22rem, calc(100vh - 10rem));
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
    transform: translateX(-50%);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 0.75rem;
    background: rgba(18, 21, 28, 0.96);
    box-shadow: 0 1.25rem 3rem rgba(0, 0, 0, 0.36);
    backdrop-filter: blur(14px);
    padding: 1.125rem;
`;

export const MinimizedBody = styled.button`
    width: 100%;
    border: 0;
    padding: 0.75rem 0.875rem;
    background: #16181d;
    color: rgba(255, 255, 255, 0.76);
    cursor: pointer;
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.8125rem;
    font-weight: 800;
`;

export const CallStage = styled.div`
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    min-height: ${p => p.$compact ? "10rem" : "0"};
    padding: 1rem;
    background: #25272d;
    display: grid;
    grid-template-rows: minmax(0, 1fr);
    align-content: stretch;
    gap: 0.75rem;
`;

export const MediaGrid = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 0;
    display: grid;
    grid-template-columns: ${p => p.$screenShare
        ? "minmax(0, 1fr) minmax(10rem, 18rem)"
        : p.$oneToOne
            ? "repeat(2, minmax(0, 1fr))"
            : p.$count <= 1
                ? "minmax(0, 1fr)"
                : p.$count <= 4
                    ? "repeat(2, minmax(0, 1fr))"
                    : "repeat(3, minmax(0, 1fr))"};
    grid-auto-rows: minmax(0, 1fr);
    gap: 0.625rem;
    align-items: center;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

export const VideoSlot = styled.div`
    width: 100%;
    min-width: 0;
    min-height: 0;
    height: auto;
    max-height: 100%;
    aspect-ratio: ${p => p.$aspectRatio || "16 / 9"};
    align-self: center;
    justify-self: center;
    display: grid;
    grid-template-rows: minmax(0, 1fr);
    gap: 0.35rem;
    position: relative;
    border: 2px solid ${p => p.$activeSpeaker
        ? "rgba(91, 110, 245, 0.86)"
        : p.$cameraOff
            ? "rgba(255, 255, 255, 0.14)"
            : "transparent"};
    border-radius: 0.625rem;
    background: #0b0c10;
    overflow: hidden;
    ${p => p.$pip && `
        position: absolute;
        right: 1rem;
        bottom: 1rem;
        z-index: 2;
        width: min(14rem, 34%);
        min-width: 9rem;
        height: 12rem;
        min-height: 12rem;
        filter: drop-shadow(0 1rem 1.5rem rgba(0, 0, 0, 0.32));
    `}
    ${p => p.$screenPrimary && `
        min-height: 0;
    `}
`;

export const VideoTile = styled.video`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    border-radius: 0.5rem;
    background: #0b0c10;
    object-fit: contain;
    object-position: center;
    display: block;
    opacity: ${p => p.$hidden ? 0 : 1};
    pointer-events: none;
    filter: none;
    transform: ${p => p.$mirrored ? "scaleX(-1)" : "none"};
`;

export const VideoLabel = styled.div`
    color: rgba(255, 255, 255, 0.72);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.6875rem;
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const VideoOverlay = styled.div`
    position: absolute;
    left: 0.625rem;
    right: 0.625rem;
    bottom: 0.625rem;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    pointer-events: none;
`;

export const VideoNamePill = styled.div`
    max-width: 100%;
    min-width: 0;
    border-radius: 999px;
    background: rgba(11, 12, 16, 0.72);
    color: #fff;
    padding: 0.35rem 0.625rem;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.6875rem;
    font-weight: 900;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const VideoStatusGroup = styled.div`
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
`;

export const VideoStatusPill = styled.div`
    border-radius: 999px;
    background: ${p =>
        p.$mutedIcon
            ? "rgba(11, 12, 16, 0.72)"
            : p.$danger
                ? "rgba(240, 68, 56, 0.9)"
                : "rgba(91, 110, 245, 0.86)"};
    color: #fff;

    min-width: ${p => p.$iconOnly ? "1.45rem" : "auto"};
    min-height: ${p => p.$iconOnly ? "1.45rem" : "1.8rem"};

    padding: ${p => p.$iconOnly ? "0.2rem" : "0.35rem 0.5rem"};

    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.625rem;
    font-weight: 900;
    white-space: nowrap;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    svg {
        width: ${p => p.$iconOnly ? "0.85rem" : "1rem"};
        height: ${p => p.$iconOnly ? "0.85rem" : "1rem"};
        display: block;
    }

    span {
        width: ${p => p.$iconOnly ? "0.9rem" : "auto"};
        height: ${p => p.$iconOnly ? "0.9rem" : "auto"};
        display: flex;
        align-items: center;
        justify-content: center;
    }

    span::after {
        width: ${p => p.$iconOnly ? "1.05rem" : "1.8rem"};
        height: ${p => p.$iconOnly ? "0.11rem" : "0.18rem"};
        box-shadow: none;
    }
`;

export const ScreenShareRail = styled.div`
    min-width: 0;
    min-height: 0;
    display: grid;
    grid-auto-rows: minmax(0, 1fr);
    gap: 0.625rem;
    align-content: stretch;
    overflow: hidden;
`;

export const Details = styled.div`
    padding: 0.875rem 1rem;
    display: grid;
    gap: 0.75rem;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    min-width: 0;
    justify-items: stretch;
`;

export const IncomingPopupIdentity = styled.div`
    min-height: 6rem;
    padding: 0.25rem 0;
    display: grid;
    place-items: center;
    gap: 0.5rem;
`;

export const IncomingPopupAvatarStack = styled.div`
    min-height: 4.75rem;
    display: flex;
    align-items: center;
    justify-content: center;

    > * + * {
        margin-left: -1rem;
    }
`;

export const IncomingPopupAvatar = styled.div`
    width: 4.75rem;
    height: 4.75rem;
    flex-basis: 4.75rem;
    border: 3px solid #22252b;
    border-radius: 999px;
    background: ${p => p.$src ? `url("${p.$src}") center / cover no-repeat` : "linear-gradient(135deg, #0B66E4 0%, #7C3AED 100%)"};
    color: #fff;
    display: grid;
    place-items: center;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 1.35rem;
    font-weight: 900;
`;

export const IncomingPopupOverflow = styled.div`
    width: 3.25rem;
    height: 3.25rem;
    flex: 0 0 3.25rem;
    border: 3px solid #22252b;
    border-radius: 999px;
    background: #5B6EF5;
    color: #fff;
    display: grid;
    place-items: center;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 900;
`;

export const IncomingPopupName = styled.div`
    max-width: 100%;
    color: #fff;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 900;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const CollapsedNotesButton = styled.button`
    width: 2.75rem;
    height: 100%;
    min-height: 0;
    border: 0;
    border-left: 1px solid rgba(255, 255, 255, 0.08);
    background: #181b22;
    color: rgba(255, 255, 255, 0.78);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 1.5rem;
    font-weight: 900;
    line-height: 1;

    &:hover {
        background: #20232b;
        color: #fff;
    }

    &:focus-visible {
        outline: 2px solid #5B6EF5;
        outline-offset: -3px;
    }
`;

export const BottomControlBar = styled.div`
    position: relative;
    z-index: 10;
    padding: 1.25rem 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(11, 12, 16, 0.86);
    display: grid;
    gap: 0.625rem;
    justify-items: center;
`;

export const BottomControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.75rem;
`;

export const ControlLabel = styled.span`
    color: rgba(255, 255, 255, 0.62);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.6875rem;
    font-weight: 900;
`;

export const Detail = styled.div`
    color: rgba(255, 255, 255, 0.72);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.8125rem;
    line-height: 1.45;
`;

export const Actions = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
`;

export const ActionButton = styled.button`
    min-height: 2.4rem;
    border: 1px solid ${p => p.$variant === "danger" ? "#F04438" : "#5B6EF5"};
    border-radius: 999px;
    background: ${p => p.$variant === "danger" ? "#F04438" : "#5B6EF5"};
    color: #fff;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.8125rem;
    font-weight: 800;
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    opacity: ${p => p.disabled ? 0.65 : 1};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
`;

export const SecondaryButton = styled(ActionButton)`
    grid-column: ${p => p.$inline ? "auto" : "1 / -1"};
    border-color: rgba(255, 255, 255, 0.16);
    background: rgba(255, 255, 255, 0.1);
`;

export const ActionIcon = styled.img`
    width: 1.375rem;
    height: 1.375rem;
    display: block;
    object-fit: contain;
    animation: none;
    transform: none;
    filter: none;
`;

export const SettingsPanel = styled.div`
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    justify-self: center;
    border-radius: 0.75rem;
    padding: 0.175rem;
    display: grid;
    gap: 0.75rem;
`;

export const SettingsTitle = styled.div`
    color: #fff;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 900;
`;

export const CapabilityList = styled.div`
    display: grid;
    gap: 0.5rem;
`;

export const CapabilityRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.625rem;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.5rem;
    background: rgba(255, 255, 255, 0.045);
    padding: 0.55rem 0.625rem;
`;

export const CapabilityText = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.125rem;
`;

export const CapabilityName = styled.div`
    color: rgba(255, 255, 255, 0.86);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.75rem;
    font-weight: 900;
`;

export const CapabilityDetail = styled.div`
    color: rgba(255, 255, 255, 0.55);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.6875rem;
    line-height: 1.35;
`;

export const CapabilityStatus = styled.span`
    border-radius: 999px;
    background: ${p => p.$supported ? "rgba(34, 197, 94, 0.18)" : "rgba(245, 158, 11, 0.16)"};
    color: ${p => p.$supported ? "#86EFAC" : "#FCD34D"};
    padding: 0.25rem 0.45rem;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.625rem;
    font-weight: 900;
    white-space: nowrap;
`;

export const LobbyLayout = styled.div`
    min-height: calc(100vh - 3.25rem);
    padding: clamp(1rem, 3vw, 2.5rem);
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(18rem, 22rem);
    justify-content: center;
    gap: 1.25rem;
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

export const LobbyMain = styled.div`
    min-width: 0;
    display: grid;
    gap: 1rem;
    align-content: start;
`;

export const LobbyHeading = styled.div`
    display: grid;
    gap: 0.35rem;
`;

export const IncomingCallSummary = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 0.875rem;
    background: rgba(255, 255, 255, 0.06);
`;

export const IncomingCallAvatar = styled.div`
    width: 3.25rem;
    height: 3.25rem;
    border-radius: 999px;
    background: ${p => p.$src ? `url("${p.$src}") center / cover no-repeat` : "linear-gradient(135deg, #0B66E4 0%, #7C3AED 100%)"};
    color: #fff;
    display: grid;
    place-items: center;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 1rem;
    font-weight: 900;
    flex: 0 0 auto;
`;

export const IncomingCallText = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.15rem;
`;

export const LobbyTitle = styled.div`
    color: #fff;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: clamp(1.35rem, 3vw, 2rem);
    font-weight: 900;
`;

export const LobbySubtitle = styled.div`
    color: rgba(255, 255, 255, 0.64);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.9375rem;
    line-height: 1.45;
`;

export const PreviewPanel = styled.div`
    position: relative;
    min-height: clamp(18rem, 50vh, 32rem);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 1rem;
    background: ${p => p.$background || "#0c0f16"};
    background-size: cover;
    background-position: center;
    overflow: hidden;
    display: grid;
    place-items: center;
`;

export const DeviceRow = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.625rem;
`;

export const LobbyFooter = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.625rem;

    @media (max-width: 520px) {
        grid-template-columns: 1fr;
    }
`;

export const PreviewVideo = styled.video`
    width: 100%;
    height: 100%;
    min-height: inherit;
    object-fit: cover;
    filter: none;
    transform: scaleX(-1);
    opacity: 1;
`;

export const PreviewFallback = styled.div`
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    display: grid;
    place-items: center;
    gap: 0.875rem;
    text-align: center;
    color: rgba(255, 255, 255, 0.72);
    font-family: Manrope, Helvetica, sans-serif;
    font-weight: 800;
`;

export const PreviewAvatar = styled.div`
    width: 6rem;
    height: 6rem;
    border-radius: 999px;
    background: ${p => p.$src ? `url("${p.$src}") center / cover no-repeat` : "linear-gradient(135deg, #0B66E4 0%, #7C3AED 100%)"};
    color: #fff;
    display: grid;
    place-items: center;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 2rem;
    font-weight: 900;
`;

export const PreviewControls = styled.div`
    position: absolute;
    left: 50%;
    bottom: 1rem;
    transform: translateX(-50%);
    display: inline-flex;
    gap: 0.75rem;
    padding: 0.5rem;
    border-radius: 999px;
    background: rgba(11, 12, 16, 0.74);
    backdrop-filter: blur(12px);
`;

export const RoundControl = styled.button`
    width: 3rem;
    height: 3rem;
    border: 0;
    border-radius: 999px;
    background: ${p => p.$danger ? "#F04438" : p.$active ? "#5B6EF5" : "rgba(255, 255, 255, 0.14)"};
    color: #fff;
    cursor: pointer;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.75rem;
    font-weight: 900;
    display: inline-grid;
    place-items: center;
    position: relative;

    &:focus-visible {
        outline: 3px solid rgba(91, 110, 245, 0.42);
        outline-offset: 2px;
    }
`;

export const ControlIcon = styled.span`
    width: 1.45rem;
    height: 1.45rem;
    display: inline-grid;
    place-items: center;
    position: relative;

    svg {
        width: 1.45rem;
        height: 1.45rem;
        display: block;
    }

    ${p => p.$disabled && `
        &::after {
            content: "";
            position: absolute;
            width: 1.8rem;
            height: 0.18rem;
            border-radius: 999px;
            background: #FF3B30;
            transform: rotate(-42deg);
            box-shadow: 0 0 0 0.08rem rgba(11, 12, 16, 0.5);
        }
    `}
`;

export const LobbySide = styled.div`
    min-width: 0;
    margin-top: ${p => p.$topAligned ? "0" : "81px"};
    max-height: calc(100vh - 6rem);
    overflow: auto;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 1rem;
    background: rgba(255, 255, 255, 0.06);
    padding: 0.875rem;
    display: grid;
    gap: 0.75rem;
    align-content: start;
    justify-items: stretch;
`;

export const FieldGroup = styled.label`
    min-width: 0;
    display: grid;
    gap: 0.35rem;
    color: rgba(255, 255, 255, 0.7);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.6875rem;
    font-weight: 900;
    text-transform: uppercase;
`;

export const DeviceSelect = styled.select`
    min-width: 0;
    width: 100%;
    min-height: 2.35rem;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 0.5rem;
    appearance: none;
    background-color: #11141b;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5 6 6.5l5-5' fill='none' stroke='%23fff' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 0.65rem auto;
    color: #fff;
    padding: 0 2.25rem 0 0.625rem;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.8125rem;
`;

export const BackgroundToggleButton = styled.button`
    width: 100%;
    min-height: 2.35rem;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 0.5rem;
    background: #11141b;
    color: #fff;
    padding: 0 0.875rem 0 0.625rem;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.8125rem;
    font-weight: 800;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    text-align: left;

    &::after {
        content: "";
        width: 0.45rem;
        height: 0.45rem;
        border-right: 1.5px solid currentColor;
        border-bottom: 1.5px solid currentColor;
        transform: rotate(${p => p.$open ? "225deg" : "45deg"});
        margin-top: ${p => p.$open ? "0.25rem" : "-0.15rem"};
        opacity: 0.74;
        flex: 0 0 auto;
    }
`;

export const EffectGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(6rem, 1fr));
    gap: 0.375rem;
`;

export const EffectButton = styled.button`
    width: 100%;
    min-height: 2.35rem;
    border: 1px solid ${p => p.$active ? "#7C8CFF" : "rgba(255, 255, 255, 0.12)"};
    border-radius: 0.45rem;
    background: ${p => p.$thumbnail || (p.$active ? "rgba(91, 110, 245, 0.24)" : "rgba(255, 255, 255, 0.07)")};
    background-size: cover;
    background-position: center;
    color: #fff;
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    opacity: ${p => p.disabled ? 0.48 : 1};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.6875rem;
    font-weight: 900;
    display: grid;
    place-items: center;
    padding: 0.375rem;
    position: relative;
    overflow: hidden;

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        background: ${p => p.$thumbnail ? "linear-gradient(180deg, rgba(10, 12, 18, 0.08), rgba(10, 12, 18, 0.58))" : "transparent"};
    }

    span {
        position: relative;
        z-index: 1;
        text-shadow: ${p => p.$thumbnail ? "0 1px 6px rgba(0, 0, 0, 0.55)" : "none"};
    }
`;

export const CustomEffectTile = styled.div`
    position: relative;
    min-width: 0;
`;

export const CustomEffectRemoveButton = styled.button`
    position: absolute;
    top: -5px;
    right: -5px;
    z-index: 3;
    width: .850rem;
    height: .850rem;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: #fff;
    color: #000;
    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1;

    &:hover {
        background: rgba(11, 12, 16, 0.9);
        color: #fff;
    }
`;

export const BackgroundPopover = styled.div`
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    max-width: 100%;
    max-height: min(18rem, calc(100vh - 11rem));
    overflow-x: hidden;
    overflow-y: auto;
    justify-self: center;
    box-shadow: 0 1rem 2.5rem rgba(0, 0, 0, 0.28);
    display: grid;
    gap: 0.5rem;
`;

export const BackgroundPopoverHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
`;

export const BackgroundPopoverTitle = styled.div`
    color: #fff;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.8125rem;
    font-weight: 900;
`;

export const BackgroundPopoverText = styled.div`
    color: rgba(255, 255, 255, 0.62);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.6875rem;
    line-height: 1.4;
`;

export const BackgroundUploadInput = styled.input`
    display: none;
`;
